import { useEffect, useState } from 'react';
import { CtaButton, ModalContainer } from '@tassomutuo-backoffice/ui';
import {
  BankInfo,
  KeyPeople,
  ShareHolders,
} from '@tassomutuo-backoffice/types';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import BankInfoInputsForm from './BankInfoInputsForm';

interface IFormInputs {
  company_info: {
    group: string;
    official_website: string;
    founded: string;
    employees: number | string;
    headquarters: string;
    branches_in_italy: number | string;
    key_people: KeyPeople[];
  };
  financial_info: {
    revenue: number | string;
    net_profit: number | string;
    shareholders: ShareHolders[];
  };
  contact_info: {
    email: string;
    phone: string;
  };
}

interface IProps {
  open: boolean;
  handleClose: () => void;
  bankInfoToUpdate: BankInfo | null;
  onSubmitBankInfoToUpdate: (
    data: IFormInputs & { bankInfoId: string }
  ) => Promise<void>;
  onSubmitBankInfoToCreate: (data: IFormInputs) => Promise<void>;
}

const ModalBankInfoForm = ({
  open,
  bankInfoToUpdate,
  handleClose,
  onSubmitBankInfoToUpdate,
  onSubmitBankInfoToCreate,
}: IProps) => {
  const getDefaultValues = (
    bankInfoToUpdate: BankInfo | null
  ): IFormInputs => ({
    company_info: {
      group: bankInfoToUpdate?.company_info?.group ?? '',
      official_website: bankInfoToUpdate?.company_info?.official_website ?? '',
      founded: bankInfoToUpdate?.company_info?.founded ?? '',
      employees: bankInfoToUpdate?.company_info?.employees ?? 0,
      headquarters: bankInfoToUpdate?.company_info?.headquarters ?? '',
      branches_in_italy: bankInfoToUpdate?.company_info?.branches_in_italy ?? 0,
      key_people: bankInfoToUpdate?.company_info?.key_people ?? [],
    },
    financial_info: {
      revenue: bankInfoToUpdate?.financial_info?.revenue ?? 0,
      net_profit: bankInfoToUpdate?.financial_info?.net_profit ?? 0,
      shareholders: bankInfoToUpdate?.financial_info?.shareholders ?? [],
    },
    contact_info: {
      email: bankInfoToUpdate?.contact_info?.email ?? '',
      phone: bankInfoToUpdate?.contact_info?.phone ?? '',
    },
  });

  const methods = useForm<IFormInputs>({
    defaultValues: getDefaultValues(bankInfoToUpdate),
  });

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    setIsLoading(true);
    try {
      if (!bankInfoToUpdate?.id) {
        await onSubmitBankInfoToCreate(data);
      } else {
        await onSubmitBankInfoToUpdate({
          ...data,
          bankInfoId: bankInfoToUpdate.id,
        });
      }
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) methods.reset(getDefaultValues(bankInfoToUpdate));
  }, [open, bankInfoToUpdate, methods]);

  return (
    <ModalContainer
      open={open}
      handleClose={handleClose}
      title="Informazioni Bancarie"
      containerSx={{ maxWidth: '1100px', maxHeight: '100vh' }}
      mtHeader={0}
    >
      <FormProvider {...methods}>
        <Box
          width="100%"
          component="form"
          maxHeight="70vh"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <BankInfoInputsForm />
          <Box display="flex" justifyContent="end">
            <CtaButton
              sx={{ my: 4, mt: 6 }}
              variant="contained"
              type="submit"
              loading={isLoading}
            >
              SALVA
            </CtaButton>
          </Box>
        </Box>
      </FormProvider>
    </ModalContainer>
  );
};

export default ModalBankInfoForm;
